import {
    type FC,
    type ReactElement,
    type ReactNode,
    type ComponentProps,
    useRef,
    useCallback,
    useEffect,
} from 'react';
import { graphql, useFragment, type LoadMoreFn } from 'react-relay';
import { type OperationType } from 'relay-runtime';

import { QuickViewProvider } from 'dibs-buyer-product-tile/exports/QuickView.ts';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel.ts';
import { type FavoritesItemPropsType } from 'dibs-portfolio-folder/exports/FavoritesItemProvider.tsx';
import { CarouselItemTile } from '../CarouselItemTile/CarouselItemTile.tsx';
import { type ImageBackgroundColor } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';

import { type TileSize } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement.ts';
import { type ItemTilesCarousel_viewer$key } from './__generated__/ItemTilesCarousel_viewer.graphql.ts';
import { type ItemTilesCarousel_user$key } from './__generated__/ItemTilesCarousel_user.graphql.ts';
import {
    type ItemTilesCarousel_items$key,
    type ItemTilesCarousel_items$data,
} from './__generated__/ItemTilesCarousel_items.graphql.ts';

type NullableItems = (ArrayElement<ItemTilesCarousel_items$data> | null)[];

const viewerFragment = graphql`
    fragment ItemTilesCarousel_viewer on Viewer
    @argumentDefinitions(
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
    ) {
        ...CarouselItemTile_viewer @arguments(fetchFavorites: $fetchFavorites, userId: $userId)
    }
`;

const userFragment = graphql`
    fragment ItemTilesCarousel_user on User {
        ...CarouselItemTile_user
    }
`;

const itemsFragment = graphql`
    fragment ItemTilesCarousel_items on Item
    @relay(plural: true)
    @argumentDefinitions(
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        priceBookName: { type: "String" }
    ) {
        serviceId
        ...CarouselItemTile_item
            @arguments(
                fetchFavorites: $fetchFavorites
                userId: $userId
                isTrade: $isTrade
                priceBookName: $priceBookName
            )
    }
`;

export const ItemTilesCarousel: FC<{
    viewer: ItemTilesCarousel_viewer$key;
    user: ItemTilesCarousel_user$key | null | undefined;
    items: ItemTilesCarousel_items$key;
    currency: string;
    authModalLoader: $TSFixMe;
    imageBackgroundColor: ImageBackgroundColor;
    itemsToShow: number;
    carouselCapacity: number;
    totalResults: number;
    loadNext?: LoadMoreFn<OperationType>;
    tileSize?: TileSize;
    isInfinite?: boolean;
    isMobile?: boolean;
    showArrows?: boolean;
    gapSize?: 'xSmall' | 'small' | 'medium' | 'large';
    showQuickView?: boolean;
    trackingList?: string;
    fullWidth?: boolean;
    onArrowClick?: ComponentProps<typeof ScrollCarousel>['onArrowClick'];
    showFolder?: boolean;
    favoritesComponentOverrides?: ((props: FavoritesItemPropsType) => ReactElement)[];
    isSponsored?: boolean;
    onClick?: (itemId: string | null | undefined) => void;
    onContentVisible?: (itemId: string | null | undefined) => void;
    dataTn?: string;
    tileClassName?: string;
    arrowVariant?: ComponentProps<typeof ScrollCarousel>['arrowVariant'];
    trailingItem?: ReactNode;
    imageWithPrice?: boolean;
    shouldAdjustFlexBasis?: boolean;
    itemFlexBasis?: string;
    stepSize?: number;
    scrollPadding?: ComponentProps<typeof ScrollCarousel>['scrollPadding'];
    edgeFade?: boolean;
}> = ({
    viewer: viewerRef,
    user: userRef,
    items: itemsRef,
    currency,
    authModalLoader,
    imageBackgroundColor,
    itemsToShow,
    carouselCapacity,
    totalResults,
    loadNext,
    tileSize,
    isInfinite,
    isMobile,
    showArrows,
    gapSize = 'large',
    showQuickView = false,
    trackingList,
    fullWidth = false,
    onArrowClick,
    showFolder,
    favoritesComponentOverrides,
    isSponsored = false,
    onClick,
    onContentVisible,
    dataTn,
    tileClassName,
    arrowVariant,
    trailingItem,
    imageWithPrice = false,
    shouldAdjustFlexBasis,
    itemFlexBasis,
    stepSize,
    scrollPadding,
    edgeFade,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const items = useFragment(itemsFragment, itemsRef);

    const remainingItems =
        carouselCapacity > totalResults
            ? totalResults - items.length
            : carouselCapacity - items.length;

    const fillerArr = remainingItems > 0 ? Array(remainingItems).fill(null) : [];
    const carouselItems: NullableItems = [...items, ...fillerArr].slice(0, carouselCapacity);

    const isLoadNextCalled = useRef(false);
    const trackedImpressionsRef = useRef<Set<string>>(new Set());
    const carouselContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        //If the parent query triggers refetch it will dispose loaded next items.
        //If items were loaded and disposed we need to load them again, to prevent infinitely showing shimmer.
        if (isLoadNextCalled.current && remainingItems && loadNext) {
            loadNext(remainingItems);
        }
    }, [remainingItems, loadNext]);

    const onSlideChange = useCallback(() => {
        if (!isLoadNextCalled.current && loadNext && remainingItems) {
            isLoadNextCalled.current = true;
            loadNext(remainingItems);
        }
    }, [loadNext, remainingItems]);

    if (carouselItems.length <= 0) {
        return null;
    }

    const carousel = (
        <QuickViewProvider elementRef={carouselContainerRef}>
            <ScrollCarousel
                animation="slide"
                enableScroll
                isInfinite={isInfinite}
                showArrows={showArrows}
                itemsToShow={itemsToShow}
                stepSize={stepSize ?? Math.floor(itemsToShow)}
                shouldAdjustFlexBasis={shouldAdjustFlexBasis}
                itemFlexBasis={itemFlexBasis}
                edgeFade={edgeFade}
                onSlideChange={onSlideChange}
                gapSize={gapSize}
                scrollPadding={scrollPadding ?? (fullWidth ? 'small' : 'none')}
                onArrowClick={onArrowClick}
                noGap
                carouselContainerRef={carouselContainerRef}
                arrowVariant={arrowVariant}
            >
                {carouselItems.map((item, index) => (
                    <CarouselItemTile
                        key={item?.serviceId || index}
                        viewer={viewer}
                        user={user}
                        item={item}
                        currency={currency}
                        authModalLoader={authModalLoader}
                        imageBackgroundColor={imageBackgroundColor}
                        index={index}
                        isMobile={isMobile || false}
                        lazyLoadStartIndex={itemsToShow}
                        tileSize={tileSize}
                        showQuickView={showQuickView}
                        trackingList={trackingList}
                        showFolder={showFolder}
                        favoritesComponentOverride={favoritesComponentOverrides?.[index]}
                        trackedImpressionsRef={trackedImpressionsRef}
                        dataTn={dataTn}
                        tileClassName={tileClassName}
                        isSponsored={isSponsored}
                        onClick={onClick}
                        onContentVisible={onContentVisible}
                        imageWithPrice={imageWithPrice}
                    />
                ))}
                {trailingItem}
            </ScrollCarousel>
        </QuickViewProvider>
    );

    if (fullWidth) {
        return <div className="primary-viewport-width">{carousel}</div>;
    }

    return carousel;
};
